/**
 * UnifiedSearchScreenView — presentational render for UnifiedSearchScreen.
 * Extracted verbatim from screens/UnifiedSearchScreen.jsx (god-file decomposition).
 * Pure view: receives all state/handlers/derived values as props. Completeness
 * machine-verified by scripts/check-extraction.js; the call site is generated
 * from the SAME prop list, so it cannot drift out of sync.
 *
 * The Book dropdown lists the major sportsbooks (it referenced an undefined
 * `keySportsbooks` until Oct 2026, which crashed that branch).
 */

import { View, Text, TextInput, ScrollView, Pressable, Platform, Animated } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import NavigationHeader from './NavigationHeader';
import { ScreenBackground } from './ThemeGradients';
import GradientButton from './GradientButton';
import Dropdown from './Dropdown';
import AutocompleteDropdown from './AutocompleteDropdown';
import { MAJOR_SPORTSBOOKS as keySportsbooks } from '../../utils/sportsbookTiers';
import SearchSuggestionsDropdown from './SearchSuggestionsDropdown';
import AdminScoreWidget from './AdminScoreWidget';
import QueryResolutionDisplay from './QueryResolutionDisplay';
import NoResultsFeedback from './NoResultsFeedback';
import { styles } from '../screens/unifiedSearchScreenStyles';


export default function UnifiedSearchScreenView({ ALL_SPORTSBOOKS, BOOST_OPTIONS, activePlayerFilter, activeSection, book, boostPercent, cacheStatus, clearAllFilters, clearSportsbookSelections, currentOptions, currentPage, currentQueryId, evRange, executeSearch, filteredResults, fixtureInfo, future, injuryAlert, isAdmin, kellyCriterion, league, live, loading, market, presentSections, queryAssessment, queryResolution, renderResultRow, resolvedSearchText, resultType, results, searchInputFocused, searchInputRef, searchQuery, selectPopularSportsbooks, selectedBets, selectedSportsbooks, sendToSGPBuilder, setActivePlayerFilter, setActiveSection, setBook, setBoostPercent, setCurrentPage, setEvRange, setFuture, setKellyCriterion, setLeague, setLive, setMarket, setResolvedSearchText, setSearchInputFocused, setSearchQuery, setShowAdvanced, setShowOriginalHint, setShowSearchSuggestions, setShowSportsbooksDropdown, setSport, setStartTime, showAdvanced, showOriginalHint, showSearchSuggestions, showSportsbooksDropdown, sport, startTime, timeRanges, toggleSportsbook, totalHeaderHeight, totalResults, totalSportsbooks, uniquePlayers, wipeAnim, wipeComplete }) {
    return (
        <View style={styles.container}>
            <ScreenBackground />
            <NavigationHeader />
            
            <ScrollView style={[styles.content, { 
                paddingTop: Platform.OS === 'web' ? (totalHeaderHeight + 20) : 20 
            }]} showsVerticalScrollIndicator={false}>
                {/* Main Search Interface */}
                <View style={styles.searchSection}>
                    <Text style={styles.sectionTitle}>Betting Search</Text>
                    
                    {/* Primary Controls Row 1 */}
                    <View style={styles.controlRow}>
                        <View style={styles.controlGroup}>
                            <AutocompleteDropdown
                                label="Sport"
                                options={['All Sports', 'Basketball', 'Football', 'Baseball', 'Hockey', 'Soccer', 'Tennis', 'Golf']}
                                value={sport === 'all' ? 'All Sports' : sport.charAt(0).toUpperCase() + sport.slice(1)}
                                onValueChange={(value) => {
                                    const sportValue = value === 'All Sports' ? 'all' : value.toLowerCase();
                                    setSport(sportValue);
                                    setLeague('all'); // Reset league when sport changes
                                    setMarket('all'); // Reset market when sport changes
                                }}
                                placeholder="Type or select sport..."
                            />
                        </View>
                        
                        <View style={styles.controlGroup}>
                            <AutocompleteDropdown
                                label="League"
                                options={['All Leagues', ...currentOptions.leagues]}
                                value={league === 'all' ? 'All Leagues' : league.toUpperCase()}
                                onValueChange={(value) => {
                                    setLeague(value === 'All Leagues' ? 'all' : value.toLowerCase());
                                }}
                                placeholder="Type or select league..."
                            />
                        </View>
                    </View>
                    
                    {/* Primary Controls Row 2 */}
                    <View style={styles.controlRow}>
                        <View style={styles.controlGroup}>
                            <AutocompleteDropdown
                                label="Market"
                                options={['All Markets', ...currentOptions.markets]}
                                value={market === 'all' ? 'All Markets' : market.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                onValueChange={(value) => {
                                    setMarket(value === 'All Markets' ? 'all' : value.toLowerCase().replace(/\s+/g, '_'));
                                }}
                                placeholder="Type or select market..."
                            />
                        </View>
                        
                        <View style={styles.controlGroup}>
                            <AutocompleteDropdown
                                label="Book"
                                options={['All Books', ...keySportsbooks]}
                                value={book === 'all' ? 'All Books' : book.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                onValueChange={(value) => {
                                    setBook(value === 'All Books' ? 'all' : value.toLowerCase().replace(/\s+/g, '_'));
                                }}
                                placeholder="Type or select book..."
                            />
                        </View>
                        
                        <View style={styles.controlGroup}>
                            <Text style={styles.controlLabel}>Start Time</Text>
                            <Dropdown
                                options={timeRanges}
                                value={startTime}
                                onValueChange={setStartTime}
                                placeholder="Select time"
                            />
                        </View>
                    </View>
                    
                    {/* Primary Controls Row 3: Boost and Sportsbooks */}
                    <View style={styles.controlRow}>
                        <View style={[styles.controlGroup, { flex: 0.5 }]}>
                            <Text style={styles.controlLabel}>Profit Boost %</Text>
                            <Dropdown
                                options={BOOST_OPTIONS}
                                value={boostPercent}
                                onValueChange={setBoostPercent}
                                placeholder="Select boost"
                            />
                        </View>
                        
                        <View style={[styles.controlGroup, { flex: 1.5 }]}>
                            <Text style={styles.controlLabel}>Sportsbooks ({selectedSportsbooks.length} selected)</Text>
                            <Pressable onPress={() => setShowSportsbooksDropdown(!showSportsbooksDropdown)}>
                                {({ hovered }) => (
                                    <View style={[styles.sportsbooksSelector, hovered && styles.sportsbooksSelectorHovered]}>
                                        <Text style={styles.sportsbooksSelectorText} numberOfLines={1}>
                                            {selectedSportsbooks.length === 0 
                                                ? 'All Sportsbooks (click to filter)' 
                                                : selectedSportsbooks.slice(0, 3).join(', ') + (selectedSportsbooks.length > 3 ? ` +${selectedSportsbooks.length - 3} more` : '')
                                            }
                                        </Text>
                                        <Ionicons name={showSportsbooksDropdown ? 'chevron-up' : 'chevron-down'} size={12} color="rgba(255,255,255,0.6)" style={{ marginLeft: 8 }} />
                                    </View>
                                )}
                            </Pressable>
                            
                            {showSportsbooksDropdown && (
                                <View style={styles.sportsbooksDropdown}>
                                    <View style={styles.sportsbooksDropdownHeader}>
                                        <Pressable onPress={selectPopularSportsbooks}>
                                            {({ hovered }) => (
                                                <View style={[styles.sportsbooksQuickAction, hovered && styles.sportsbooksQuickActionHovered]}>
                                                    <Text style={styles.sportsbooksQuickActionText}>Select Popular</Text>
                                                </View>
                                            )}
                                        </Pressable>
                                        <Pressable onPress={clearSportsbookSelections}>
                                            {({ hovered }) => (
                                                <View style={[styles.sportsbooksQuickAction, { backgroundColor: hovered ? 'rgba(248,113,113,0.35)' : 'rgba(248,113,113,0.2)' }, hovered && { borderColor: 'rgba(248,113,113,0.6)' }]}>
                                                    <Text style={[styles.sportsbooksQuickActionText, { color: '#f87171' }]}>Clear All</Text>
                                                </View>
                                            )}
                                        </Pressable>
                                        <Pressable onPress={() => setShowSportsbooksDropdown(false)}>
                                            {({ hovered }) => (
                                                <View style={[styles.sportsbooksCloseBtn, hovered && styles.sportsbooksCloseBtnHovered]}>
                                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                                        <Text style={styles.sportsbooksCloseBtnText}>Done</Text>
                                                        <Ionicons name="checkmark" size={12} color="#4ade80" />
                                                    </View>
                                                </View>
                                            )}
                                        </Pressable>
                                    </View>
                                    
                                    <View style={styles.sportsbooksList}>
                                        {[
                                            { tier: 1, label: 'Most Popular', icon: 'star' },
                                            { tier: 2, label: 'Well-Known', icon: null },
                                            { tier: 3, label: 'Offshore / Sharp', icon: null },
                                            { tier: 4, label: 'Regional / Newer', icon: null },
                                            { tier: 5, label: 'Alternative / New', icon: null },
                                        ].map(({ tier, label, icon }) => (
                                            <React.Fragment key={tier}>
                                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                                    {icon && <Ionicons name={icon} size={10} color="rgba(255,255,255,0.5)" />}
                                                    <Text style={styles.sportsbooksTierLabel}>{label}</Text>
                                                </View>
                                                <View style={styles.sportsbooksGrid}>
                                                    {ALL_SPORTSBOOKS.filter(b => b.tier === tier).map((book) => {
                                                        const isSelected = selectedSportsbooks.includes(book.name);
                                                        return (
                                                            <Pressable
                                                                key={book.name}
                                                                onPress={() => toggleSportsbook(book.name)}
                                                            >
                                                                {({ hovered }) => (
                                                                    <View style={[
                                                                        styles.sportsbookChip,
                                                                        isSelected && styles.sportsbookChipSelected,
                                                                        hovered && !isSelected && styles.sportsbookChipHovered,
                                                                    ]}>
                                                                        <Text style={[
                                                                            styles.sportsbookChipText,
                                                                            isSelected && styles.sportsbookChipTextSelected,
                                                                        ]}>
                                                                            {book.name}
                                                                        </Text>
                                                                    </View>
                                                                )}
                                                            </Pressable>
                                                        );
                                                    })}
                                                </View>
                                            </React.Fragment>
                                        ))}
                                    </View>
                                </View>
                            )}
                        </View>
                    </View>
                    
                    {/* Search Query */}
                    <View style={[styles.searchQueryContainer, { zIndex: 100, position: 'relative' }]}>
                        <Text style={styles.controlLabel}>Search Query</Text>
                        <View style={{ position: 'relative', zIndex: 100 }}>
                            <TextInput
                                ref={searchInputRef}
                                style={styles.searchInput}
                                value={searchQuery}
                                onChangeText={(text) => {
                                    setSearchQuery(text);
                                    setShowSearchSuggestions(true);
                                    // User is editing — dismiss the resolved overlay
                                    if (resolvedSearchText) setResolvedSearchText(null);
                                }}
                                onFocus={() => {
                                    setSearchInputFocused(true);
                                    if (searchQuery.trim().length >= 2) setShowSearchSuggestions(true);
                                }}
                                onBlur={() => {
                                    setSearchInputFocused(false);
                                    setTimeout(() => setShowSearchSuggestions(false), 200);
                                }}
                                placeholder="Enter search terms (e.g., 'Lakers vs Warriors tonight')"
                                placeholderTextColor="rgba(255,255,255,0.5)"
                            />

                            {/* ✨ Resolved-query overlay — sits on top of the TextInput when not focused */}
                            {resolvedSearchText && !searchInputFocused && (
                                <Pressable
                                    onPress={() => {
                                        setSearchInputFocused(true);
                                        searchInputRef.current?.focus();
                                    }}
                                    onHoverIn={() => setShowOriginalHint(true)}
                                    onHoverOut={() => setShowOriginalHint(false)}
                                    style={{
                                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                        backgroundColor: 'rgba(255,255,255,0.1)',
                                        borderRadius: 8,
                                        paddingHorizontal: 12,
                                        borderWidth: 1,
                                        borderColor: 'rgba(255,255,255,0.2)',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        cursor: 'text',
                                    }}
                                >
                                    {/* Moving scan / glow line during animation */}
                                    {!wipeComplete && (
                                        <Animated.View pointerEvents="none" style={{
                                            position: 'absolute', top: 0, bottom: 0, width: 2,
                                            left: wipeAnim.interpolate({ inputRange: [0, 1], outputRange: ['0%', '105%'] }),
                                            backgroundColor: '#818cf8',
                                            shadowColor: '#818cf8',
                                            shadowRadius: 10,
                                            shadowOpacity: 1,
                                        }} />
                                    )}

                                    {/* Original query (shown on hover) */}
                                    {showOriginalHint ? (
                                        <Text style={{ color: 'rgba(255,255,255,0.45)', fontSize: 16, fontStyle: 'italic' }}>
                                            {searchQuery}
                                        </Text>
                                    ) : (
                                        <Animated.Text style={{
                                            color: '#ffffff',
                                            fontSize: 16,
                                            fontWeight: '500',
                                            opacity: wipeAnim.interpolate({ inputRange: [0, 0.25, 1], outputRange: [0, 0.6, 1] }),
                                        }}>
                                            {resolvedSearchText}
                                            {wipeComplete && (
                                                <Text style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, fontWeight: '400' }}>
                                                    {'  '}(hover for original)
                                                </Text>
                                            )}
                                        </Animated.Text>
                                    )}
                                </Pressable>
                            )}

                            <SearchSuggestionsDropdown
                                query={searchQuery}
                                visible={showSearchSuggestions}
                                sport={sport !== 'all' ? sport : null}
                                onSelect={(text) => {
                                    setSearchQuery(text);
                                    setShowSearchSuggestions(false);
                                }}
                                onDismiss={() => setShowSearchSuggestions(false)}
                            />
                        </View>
                    </View>
                    
                    {/* Advanced Filters Toggle */}
                    <Pressable onPress={() => setShowAdvanced(!showAdvanced)}>
                        {({ hovered }) => (
                            <View style={[styles.advancedToggle, hovered && styles.advancedToggleHovered]}>
                                <Text style={[styles.advancedToggleText, hovered && { color: '#fff' }]}>
                                    {showAdvanced ? 'Hide' : 'Show'} Advanced Filters
                                </Text>
                            </View>
                        )}
                    </Pressable>
                    
                    {/* Advanced Filters Panel */}
                    {showAdvanced && (
                        <View style={styles.advancedPanel}>
                            <Text style={styles.advancedTitle}>Advanced Filters</Text>
                            
                            {/* Value Filters */}
                            <View style={styles.advancedSection}>
                                <Text style={styles.advancedSectionTitle}>Value Filters</Text>
                                <View style={styles.rangeRow}>
                                    <View style={styles.rangeGroup}>
                                        <Text style={styles.rangeLabel}>EV% Range</Text>
                                        <View style={styles.rangeInputs}>
                                            <TextInput
                                                style={styles.rangeInput}
                                                value={evRange.min}
                                                onChangeText={(text) => setEvRange(prev => ({ ...prev, min: text }))}
                                                placeholder="Min"
                                                placeholderTextColor="rgba(255,255,255,0.5)"
                                            />
                                            <Text style={styles.rangeSeparator}>to</Text>
                                            <TextInput
                                                style={styles.rangeInput}
                                                value={evRange.max}
                                                onChangeText={(text) => setEvRange(prev => ({ ...prev, max: text }))}
                                                placeholder="Max"
                                                placeholderTextColor="rgba(255,255,255,0.5)"
                                            />
                                        </View>
                                    </View>
                                    
                                    <View style={styles.rangeGroup}>
                                        <Text style={styles.rangeLabel}>Kelly Criterion (Fractional)</Text>
                                        <View style={styles.kellyButtonsRow}>
                                            {['0.125', '0.25', '0.5', '1.0'].map((val) => (
                                                <Pressable
                                                    key={val}
                                                    onPress={() => setKellyCriterion(val)}
                                                >
                                                    {({ hovered }) => (
                                                        <View style={[
                                                            styles.kellyPresetButton,
                                                            kellyCriterion === val && styles.kellyPresetButtonActive,
                                                            hovered && kellyCriterion !== val && styles.kellyPresetButtonHovered,
                                                        ]}>
                                                            <Text style={[
                                                                styles.kellyPresetText,
                                                                kellyCriterion === val && styles.kellyPresetTextActive,
                                                            ]}>
                                                                {val === '0.125' ? '1/8' : val === '0.25' ? '1/4' : val === '0.5' ? '1/2' : 'Full'}
                                                            </Text>
                                                        </View>
                                                    )}
                                                </Pressable>
                                            ))}
                                            <TextInput
                                                value={kellyCriterion}
                                                onChangeText={setKellyCriterion}
                                                placeholder="Custom"
                                                placeholderTextColor="rgba(255,255,255,0.4)"
                                                style={styles.kellyCustomInput}
                                            />
                                        </View>
                                        <Text style={styles.kellyHint}>
                                            1/4 Kelly (recommended) • 1/2 Kelly (moderate) • Full Kelly (aggressive)
                                        </Text>
                                    </View>
                                </View>
                            </View>
                            
                            {/* Additional Filters */}
                            <View style={styles.advancedSection}>
                                <Text style={styles.advancedSectionTitle}>Additional Filters</Text>
                                <View style={styles.checkboxRow}>
                                    <Pressable onPress={() => setFuture(!future)}>
                                        {({ hovered }) => (
                                            <View style={[styles.checkbox, future && styles.checkboxActive, hovered && !future && styles.checkboxHovered]}>
                                                <Text style={[styles.checkboxText, future && styles.checkboxTextActive]}>
                                                    Future Bets
                                                </Text>
                                            </View>
                                        )}
                                    </Pressable>
                                    
                                    <Pressable onPress={() => setLive(!live)}>
                                        {({ hovered }) => (
                                            <View style={[styles.checkbox, live && styles.checkboxActive, hovered && !live && styles.checkboxHovered]}>
                                                <Text style={[styles.checkboxText, live && styles.checkboxTextActive]}>
                                                    Live Events
                                                </Text>
                                            </View>
                                        )}
                                    </Pressable>
                                </View>
                            </View>
                        </View>
                    )}
                    
                    {/* Selected Sportsbooks Summary (if any selected) */}
                    {selectedSportsbooks.length > 0 && (
                        <View style={styles.selectedSportsbooksSection}>
                            <View style={styles.selectedSportsbooksHeader}>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                                    <Ionicons name="filter" size={12} color="rgba(255,255,255,0.7)" />
                                    <Text style={styles.selectedSportsbooksLabel}>
                                        Filtering by {selectedSportsbooks.length} sportsbook{selectedSportsbooks.length !== 1 ? 's' : ''}:
                                    </Text>
                                </View>
                                <Pressable onPress={clearSportsbookSelections}>
                                    {({ hovered }) => (
                                        <Text style={[styles.clearSportsbooksBtn, hovered && { color: '#fca5a5' }]}>Clear</Text>
                                    )}
                                </Pressable>
                                    </View>
                            <View style={styles.selectedSportsbooksChips}>
                                {selectedSportsbooks.map((book, idx) => (
                                    <Pressable key={idx} onPress={() => toggleSportsbook(book)}>
                                        {({ hovered }) => (
                                            <View style={[styles.selectedSportsbookChip, hovered && styles.selectedSportsbookChipHovered]}>
                                                <Text style={styles.selectedSportsbookChipText}>{book}</Text>
                                                <Text style={[styles.selectedSportsbookRemove, hovered && { color: '#f87171' }]}>×</Text>
                                            </View>
                                        )}
                                    </Pressable>
                                ))}
                                    </View>
                        </View>
                    )}
                    
                    {/* Action Buttons */}
                    <View style={styles.actionButtons}>
                        <GradientButton
                            text="Search"
                            onPress={executeSearch}
                            style={styles.searchButton}
                            gradientColors={["#707070", "#FFFFFF"]}
                            loading={loading}
                        />
                        <Pressable onPress={clearAllFilters}>
                            {({ hovered }) => (
                                <View style={[styles.clearButton, hovered && styles.clearButtonHovered]}>
                                    <Text style={[styles.clearButtonText, hovered && { color: '#fff' }]}>Clear Search Filters</Text>
                                </View>
                            )}
                        </Pressable>
                    </View>
                    
                    {/* Cache Status Display */}
                    {loading && cacheStatus && (
                        <View style={[
                            styles.cacheStatusContainer,
                            cacheStatus.stage === 'cache_miss' && styles.cacheStatusHighlight
                        ]}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                                {cacheStatus.stage === 'checking_cache' && <Ionicons name="search-outline" size={13} color="rgba(255,255,255,0.6)" />}
                                {cacheStatus.stage === 'cache_hit' && <Ionicons name="flash" size={13} color="#fbbf24" />}
                                {cacheStatus.stage === 'cache_miss' && <Ionicons name="search" size={13} color="rgba(255,255,255,0.6)" />}
                                {cacheStatus.stage === 'error' && <Ionicons name="warning-outline" size={13} color="#f87171" />}
                                <Text style={styles.cacheStatusText}>{cacheStatus.message}</Text>
                            </View>
                        </View>
                    )}
                    
                    {/* No Data Message Display */}
                    {!loading && cacheStatus && (cacheStatus.stage === 'info' || cacheStatus.stage === 'error') && (
                        <View style={[styles.cacheStatusContainer, { backgroundColor: 'rgba(255, 255, 255, 0.08)', marginTop: 12 }]}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                {cacheStatus.stage === 'info'
                                    ? <Ionicons name="information-circle-outline" size={16} color="rgba(255,255,255,0.7)" />
                                    : <Ionicons name="warning-outline" size={16} color="#f87171" />
                                }
                                <Text style={[styles.cacheStatusText, { fontSize: 14 }]}>{cacheStatus.message}</Text>
                            </View>
                        </View>
                    )}

                    {/* No Results Feedback — show interpretation + correction form */}
                    {!loading && results.length === 0 && queryResolution && searchQuery && (
                        <NoResultsFeedback
                            queryResolution={queryResolution}
                            originalQuery={searchQuery}
                            message={cacheStatus?.message}
                        />
                    )}

                    {/* Injury Alert Banner */}
                    {!loading && injuryAlert && (
                        <View style={[styles.cacheStatusContainer, {
                            backgroundColor: injuryAlert.status?.toLowerCase() === 'out' ? 'rgba(255, 59, 48, 0.15)'
                                : injuryAlert.status?.toLowerCase() === 'doubtful' ? 'rgba(255, 149, 0, 0.15)'
                                : 'rgba(255, 204, 0, 0.15)',
                            borderColor: injuryAlert.status?.toLowerCase() === 'out' ? 'rgba(255, 59, 48, 0.4)'
                                : injuryAlert.status?.toLowerCase() === 'doubtful' ? 'rgba(255, 149, 0, 0.4)'
                                : 'rgba(255, 204, 0, 0.4)',
                            marginTop: 8,
                        }]}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                <Ionicons
                                    name="medical"
                                    size={14}
                                    color={injuryAlert.status?.toLowerCase() === 'out' ? '#FF6B6B' : injuryAlert.status?.toLowerCase() === 'doubtful' ? '#FFB347' : '#FFD93D'}
                                />
                                <Text style={{
                                    color: injuryAlert.status?.toLowerCase() === 'out' ? '#FF6B6B'
                                        : injuryAlert.status?.toLowerCase() === 'doubtful' ? '#FFB347'
                                        : '#FFD93D',
                                    fontSize: 14, fontWeight: '600', textAlign: 'center',
                                }}>
                                    {injuryAlert.status?.toUpperCase()}{injuryAlert.bodyPart ? ` — ${injuryAlert.bodyPart}` : ''}{injuryAlert.details ? `: ${injuryAlert.details}` : ''}
                                </Text>
                            </View>
                        </View>
                    )}
                    
                    {/* Query Resolution Display */}
                    {queryResolution && !loading && results.length > 0 && (
                        <QueryResolutionDisplay
                            originalQuery={searchQuery}
                            responseMetadata={{ _queryResolution: queryResolution }}
                            autoAnimate={true}
                            animationDelay={300}
                            compact={true}
                            style={{ marginBottom: 8, marginTop: 4 }}
                        />
                    )}
                </View>
                
                {/* Results Section */}
                {results.length > 0 && (
                    <View style={styles.resultsSection}>
                        {/* ⭐ Admin Score Widget - only visible to admins */}
                        {isAdmin && currentQueryId && (
                            <AdminScoreWidget
                                queryId={currentQueryId}
                                autoScore={queryAssessment?.autoScore}
                                autoGrade={queryAssessment?.autoGrade}
                                issues={queryAssessment?.issues || []}
                                positives={queryAssessment?.positives || []}
                                onScoreSubmitted={(data) => {
                                    console.log('[UnifiedSearch] Admin score submitted:', data);
                                }}
                            />
                        )}
                        
                        {/* Matchup H2H Fixture Header */}
                        {resultType === 'MATCHUP_H2H' && fixtureInfo && (
                            <View style={{ backgroundColor: '#1e293b', borderRadius: 10, padding: 16, marginBottom: 12, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 16, borderWidth: 1, borderColor: 'rgba(59,130,246,0.3)' }}>
                                <Text style={{ color: '#ffffff', fontSize: 18, fontWeight: '700' }}>
                                    {fixtureInfo.away_team || ''}
                                </Text>
                                <View style={{ backgroundColor: 'rgba(59,130,246,0.2)', borderRadius: 6, paddingHorizontal: 12, paddingVertical: 4 }}>
                                    <Text style={{ color: '#3b82f6', fontSize: 14, fontWeight: '600' }}>vs</Text>
                                </View>
                                <Text style={{ color: '#ffffff', fontSize: 18, fontWeight: '700' }}>
                                    {fixtureInfo.home_team || ''}
                                </Text>
                                {fixtureInfo.start_date && (
                                    <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, marginLeft: 8 }}>
                                        {new Date(fixtureInfo.start_date).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </Text>
                                )}
                            </View>
                        )}


                        <View style={styles.resultsHeader}>
                            <Text style={styles.resultsTitle}>
                                Results ({filteredResults.length !== totalResults ? `${filteredResults.length} shown · ${totalResults} total` : `${totalResults} found`})
                                {totalSportsbooks > 0 && (
                                    <Text style={styles.sportsbookCountBadge}>
                                        {' '}• {totalSportsbooks} sportsbooks
                                    </Text>
                                )}
                            </Text>
                            <Text style={styles.resultsSubtitle}>
                                Page {currentPage} of {Math.max(1, Math.ceil(filteredResults.length / 10))}
                            </Text>
                        </View>

                        {/* Section Filter Tabs — only shown when multiple sections are present */}
                        {presentSections.length > 1 && (() => {
                            const sectionMeta = {
                                game_lines:   { label: 'Team Odds',    color: '#3b82f6' },
                                player_props: { label: 'Player Props', color: '#8b5cf6' },
                                derivatives:  { label: 'Derivatives',  color: '#f59e0b' },
                            };
                            const tabs = ['all', ...presentSections];
                            return (
                                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 8 }} contentContainerStyle={{ paddingHorizontal: 12, gap: 8, flexDirection: 'row', alignItems: 'center', paddingVertical: 6 }}>
                                    {tabs.map(tab => {
                                        const isActive = activeSection === tab;
                                        const meta = sectionMeta[tab];
                                        const color = meta?.color || '#6b7280';
                                        const label = tab === 'all' ? 'All' : (meta?.label || tab);
                                        return (
                                            <Pressable
                                                key={tab}
                                                onPress={() => { setActiveSection(tab); setCurrentPage(1); setActivePlayerFilter(null); }}
                                            >
                                                {({ hovered }) => (
                                                    <View style={{
                                                        paddingHorizontal: 14,
                                                        paddingVertical: 6,
                                                        borderRadius: 20,
                                                        borderWidth: 1.5,
                                                        borderColor: isActive ? color : (hovered ? 'rgba(255,255,255,0.32)' : 'rgba(255,255,255,0.15)'),
                                                        backgroundColor: isActive ? `${color}22` : (hovered ? 'rgba(255,255,255,0.07)' : 'transparent'),
                                                    }}>
                                                        <Text style={{ color: isActive ? color : (hovered ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.5)'), fontWeight: isActive ? '700' : '500', fontSize: 12 }}>
                                                            {label}
                                                        </Text>
                                                    </View>
                                                )}
                                            </Pressable>
                                        );
                                    })}
                                </ScrollView>
                            );
                        })()}

                        {/* Player Filter Chips — shown when player props are in the visible results */}
                        {uniquePlayers.length > 0 && (activeSection === 'player_props' || (activeSection === 'all' && presentSections.includes('player_props'))) && (
                            <View style={{ marginBottom: 8 }}>
                                <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, paddingHorizontal: 12, marginBottom: 4 }}>Filter by Player</Text>
                                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 12, gap: 6, flexDirection: 'row', alignItems: 'center', paddingVertical: 4 }}>
                                    <Pressable onPress={() => { setActivePlayerFilter(null); setCurrentPage(1); }}>
                                        {({ hovered }) => (
                                            <View style={{
                                                paddingHorizontal: 12,
                                                paddingVertical: 5,
                                                borderRadius: 16,
                                                borderWidth: 1.5,
                                                borderColor: activePlayerFilter === null ? '#8b5cf6' : (hovered ? 'rgba(139,92,246,0.4)' : 'rgba(255,255,255,0.15)'),
                                                backgroundColor: activePlayerFilter === null ? 'rgba(139,92,246,0.15)' : (hovered ? 'rgba(139,92,246,0.08)' : 'transparent'),
                                            }}>
                                                <Text style={{ color: activePlayerFilter === null ? '#8b5cf6' : (hovered ? 'rgba(139,92,246,0.8)' : 'rgba(255,255,255,0.45)'), fontWeight: activePlayerFilter === null ? '700' : '400', fontSize: 12 }}>All Players</Text>
                                            </View>
                                        )}
                                    </Pressable>
                                    {uniquePlayers.map(player => (
                                        <Pressable
                                            key={player}
                                            onPress={() => { setActivePlayerFilter(activePlayerFilter === player ? null : player); setCurrentPage(1); }}
                                        >
                                            {({ hovered }) => (
                                                <View style={{
                                                    paddingHorizontal: 12,
                                                    paddingVertical: 5,
                                                    borderRadius: 16,
                                                    borderWidth: 1.5,
                                                    borderColor: activePlayerFilter === player ? '#8b5cf6' : (hovered ? 'rgba(139,92,246,0.4)' : 'rgba(255,255,255,0.15)'),
                                                    backgroundColor: activePlayerFilter === player ? 'rgba(139,92,246,0.15)' : (hovered ? 'rgba(139,92,246,0.08)' : 'transparent'),
                                                }}>
                                                    <Text style={{ color: activePlayerFilter === player ? '#8b5cf6' : (hovered ? 'rgba(139,92,246,0.8)' : 'rgba(255,255,255,0.45)'), fontWeight: activePlayerFilter === player ? '700' : '400', fontSize: 12 }}>{player}</Text>
                                                </View>
                                            )}
                                        </Pressable>
                                    ))}
                                </ScrollView>
                            </View>
                        )}
                        
                        {/* Results Table */}
                        <View style={styles.resultsTable}>
                            <View style={styles.tableHeader}>
                                <Text style={[styles.headerCell, styles.selectCell]}>Select</Text>
                                <Text style={styles.headerCell}>League</Text>
                                <Text style={styles.headerCell}>Date</Text>
                                <Text style={styles.headerCell}>Time</Text>
                                <Text style={styles.headerCell}>Game</Text>
                                <Text style={styles.headerCell}>Market</Text>
                                <Text style={styles.headerCell}>Bet</Text>
                                <Text style={styles.headerCell}>Book</Text>
                                <Text style={styles.headerCell}>Odds</Text>
                                <Text style={styles.headerCell}>Books</Text>
                                <Text style={styles.headerCell}>Fair Value</Text>
                                <Text style={styles.headerCell}>EV%</Text>
                                <Text style={styles.headerCell}>Kelly</Text>
                            </View>
                            
                            {(() => {
                                const pageItems = filteredResults.slice((currentPage - 1) * 10, currentPage * 10);
                                const rows = [];
                                let lastSection = null;
                                // Only show inline section dividers when viewing "All" (tabs already indicate section when filtered)
                                const showDividers = activeSection === 'all';
                                const sectionLabels = { game_lines: 'Team Odds', player_props: 'Player Props', derivatives: 'Derivatives' };
                                const sectionColors = { game_lines: '#3b82f6', player_props: '#8b5cf6', derivatives: '#f59e0b' };
                                pageItems.forEach((result, index) => {
                                    if (showDividers && result._resultSection && result._resultSection !== lastSection) {
                                        lastSection = result._resultSection;
                                        rows.push(
                                            <View key={`section-${result._resultSection}`} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingHorizontal: 12, backgroundColor: '#1e293b', borderLeftWidth: 3, borderLeftColor: sectionColors[result._resultSection] || '#6b7280' }}>
                                                <Text style={{ color: sectionColors[result._resultSection] || '#9ca3af', fontWeight: '700', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>{sectionLabels[result._resultSection] || result._resultSection}</Text>
                                            </View>
                                        );
                                    }
                                    rows.push(renderResultRow(result, index));
                                });
                                return rows;
                            })()}
                        </View>
                        
                        {/* Send to SGP Builder Button */}
                        {selectedBets.length > 0 && (
                            <View style={styles.sgpBuilderSection}>
                                <GradientButton
                                    text={`Send ${selectedBets.length} Bet${selectedBets.length > 1 ? 's' : ''} to SGP Builder`}
                                    onPress={sendToSGPBuilder}
                                    style={styles.sgpBuilderButton}
                                    gradientColors={["#8b5cf6", "#c4b5fd"]}
                                />
                            </View>
                        )}
                        
                        {/* Pagination */}
                        {(() => {
                            const filteredPages = Math.max(1, Math.ceil(filteredResults.length / 10));
                            if (filteredPages <= 1) return null;
                            return (
                                <View style={styles.pagination}>
                                    <Pressable
                                        onPress={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                    >
                                        {({ hovered }) => (
                                            <View style={[styles.paginationButton, currentPage === 1 && styles.paginationButtonDisabled, hovered && currentPage !== 1 && styles.paginationButtonHovered]}>
                                                <Text style={styles.paginationButtonText}>Previous</Text>
                                            </View>
                                        )}
                                    </Pressable>
                                    
                                    <Text style={styles.paginationText}>
                                        {currentPage} of {filteredPages}
                                    </Text>
                                    
                                    <Pressable
                                        onPress={() => setCurrentPage(prev => Math.min(filteredPages, prev + 1))}
                                        disabled={currentPage === filteredPages}
                                    >
                                        {({ hovered }) => (
                                            <View style={[styles.paginationButton, currentPage === filteredPages && styles.paginationButtonDisabled, hovered && currentPage !== filteredPages && styles.paginationButtonHovered]}>
                                                <Text style={styles.paginationButtonText}>Next</Text>
                                            </View>
                                        )}
                                    </Pressable>
                                </View>
                            );
                        })()}
                    </View>
                )}
            </ScrollView>
        </View>
    );
}
